import { useCallback, useEffect, useState } from 'react';

export const useIsHorizontalOverflow = (ref: React.RefObject<HTMLElement>): boolean => {
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = ref.current;
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  }, [ref]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Initial check
    setIsOverflow(checkOverflow());

    // Use ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      setIsOverflow(checkOverflow());
    });

    resizeObserver.observe(element);

    // Add MutationObserver to detect content changes (child additions/removals)
    const mutationObserver = new MutationObserver(() => {
      setIsOverflow(checkOverflow());
    });

    mutationObserver.observe(element, {
      childList: true, // Watch for child additions/removals
      subtree: true, // Watch all descendants
    });

    // Also listen to window resize as fallback
    const handleWindowResize = () => setIsOverflow(checkOverflow());
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [checkOverflow, ref]);

  return isOverflow;
};
